import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ChevronDown, Loader2, ShoppingBag } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { ORDER_SETTINGS_DEFAULTS, type OrderSettings } from "@/hooks/useOrderSettings";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type FormState = Record<string, string>;

function boolStr(v: boolean) { return v ? "true" : "false"; }

function initForm(data: Record<string, string> | undefined): FormState {
  const d = ORDER_SETTINGS_DEFAULTS;
  const defaults: FormState = {};
  for (const [k, v] of Object.entries(d)) {
    defaults[k] = typeof v === "boolean" ? boolStr(v) : String(v);
  }
  if (data) {
    for (const [k, v] of Object.entries(data)) {
      if (k.startsWith("order_")) defaults[k] = v;
    }
  }
  return defaults;
}

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <CollapsibleTrigger className="flex items-center justify-between w-full py-3 text-left group">
      <div>
        <p className="font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
    </CollapsibleTrigger>
  );
}

function FieldRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      {children}
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}

function ToggleField({ label, description, checked, onChange }: { label: string; description?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function OnlineOrderingCard() {
  const { data: allSettings, isLoading } = useAppSettings();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>({});
  const [initialized, setInitialized] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (allSettings && !initialized) {
      setForm(initForm(allSettings));
      setInitialized(true);
    }
  }, [allSettings, initialized]);

  const set = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));
  const getBool = (key: string) => form[key] === "true" || form[key] === "1";
  const setBool = (key: string, v: boolean) => set(key, boolStr(v));

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(form).filter(([k]) => k.startsWith("order_"));
      for (const [key, value] of entries) {
        const { error: updateError } = await supabase
          .from("app_settings")
          .update({ value, updated_at: new Date().toISOString() })
          .eq("key", key);

        if (updateError) {
          // Key doesn't exist yet — insert it
          await supabase.from("app_settings").insert({ key, value });
        }
      }
      qc.invalidateQueries({ queryKey: ["app_settings"] });
      toast.success("Online ordering settings saved");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !initialized) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-xl">Online Ordering</CardTitle></CardHeader>
        <CardContent><div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="text-xl">Online Ordering</CardTitle>
              <CardDescription>Control your public order page, fulfillment options, upsells, and checkout experience.</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={getBool("order_enabled") ? "default" : "secondary"}>
              {getBool("order_enabled") ? "LIVE" : "OFF"}
            </Badge>
            <Switch checked={getBool("order_enabled")} onCheckedChange={(v) => setBool("order_enabled", v)} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Page Content */}
        <Collapsible>
          <SectionHeader title="Page Content" description="Hero title, subtitle, and success messages" />
          <CollapsibleContent className="space-y-4 pb-4">
            <FieldRow label="Hero Title">
              <Input value={form.order_page_title ?? ""} onChange={e => set("order_page_title", e.target.value)} />
            </FieldRow>
            <FieldRow label="Hero Subtitle">
              <Textarea value={form.order_page_subtitle ?? ""} onChange={e => set("order_page_subtitle", e.target.value)} rows={2} />
            </FieldRow>
            <Separator />
            <FieldRow label="Success Heading" description="Shown after order is submitted">
              <Input value={form.order_success_heading ?? ""} onChange={e => set("order_success_heading", e.target.value)} />
            </FieldRow>
            <FieldRow label="Success Message">
              <Textarea value={form.order_success_message ?? ""} onChange={e => set("order_success_message", e.target.value)} rows={2} />
            </FieldRow>
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        {/* Fulfillment */}
        <Collapsible>
          <SectionHeader title="Fulfillment Options" description="Pickup/delivery toggles, labels, and flow control" />
          <CollapsibleContent className="space-y-4 pb-4">
            <ToggleField label="Enable Pickup" checked={getBool("order_pickup_enabled")} onChange={v => setBool("order_pickup_enabled", v)} />
            <ToggleField label="Enable Delivery" checked={getBool("order_delivery_enabled")} onChange={v => setBool("order_delivery_enabled", v)} />
            <Separator />
            <FieldRow label="Pickup Label">
              <Input value={form.order_pickup_label ?? ""} onChange={e => set("order_pickup_label", e.target.value)} />
            </FieldRow>
            <FieldRow label="Delivery Label">
              <Input value={form.order_delivery_label ?? ""} onChange={e => set("order_delivery_label", e.target.value)} />
            </FieldRow>
            <Separator />
            <FieldRow label="Default Fulfillment" description="Which option is pre-selected">
              <Select value={form.order_default_fulfillment ?? "pickup"} onValueChange={v => set("order_default_fulfillment", v)}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="When to ask fulfillment type" description="Upfront (before menu) or at checkout (current behavior)">
              <Select value={form.order_fulfillment_position ?? "checkout"} onValueChange={v => set("order_fulfillment_position", v)}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="checkout">At Checkout</SelectItem>
                  <SelectItem value="upfront">Upfront (before menu)</SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
            <ToggleField
              label="Collect delivery address upfront"
              description="When fulfillment is upfront and delivery is selected, ask for address immediately"
              checked={getBool("order_delivery_collect_address_upfront")}
              onChange={v => setBool("order_delivery_collect_address_upfront", v)}
            />
            <Separator />
            <FieldRow label="Delivery Fee" description="Enter a dollar amount (e.g. '15') or 'quote' to discuss on confirmation call">
              <Input value={form.order_delivery_fee ?? "quote"} onChange={e => set("order_delivery_fee", e.target.value)} className="w-48" />
            </FieldRow>
            <FieldRow label="Delivery Zone Note" description="Shown to customers who select delivery">
              <Textarea value={form.order_delivery_zone_note ?? ""} onChange={e => set("order_delivery_zone_note", e.target.value)} rows={2} />
            </FieldRow>
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        {/* Upsells */}
        <Collapsible>
          <SectionHeader title="Upsells" description="Toggle and customize upsell options shown at checkout" />
          <CollapsibleContent className="space-y-4 pb-4">
            <FieldRow label="Upsell Section Title">
              <Input value={form.order_upsell_section_title ?? ""} onChange={e => set("order_upsell_section_title", e.target.value)} />
            </FieldRow>
            <Separator />

            {/* Chafing */}
            <ToggleField label="Chafing Dishes Upsell" checked={getBool("order_upsell_chafing_enabled")} onChange={v => setBool("order_upsell_chafing_enabled", v)} />
            {getBool("order_upsell_chafing_enabled") && (
              <div className="pl-4 border-l-2 border-border space-y-3">
                <FieldRow label="Title">
                  <Input value={form.order_upsell_chafing_title ?? ""} onChange={e => set("order_upsell_chafing_title", e.target.value)} />
                </FieldRow>
                <FieldRow label="Description">
                  <Textarea value={form.order_upsell_chafing_description ?? ""} onChange={e => set("order_upsell_chafing_description", e.target.value)} rows={2} />
                </FieldRow>
              </div>
            )}
            <Separator />

            {/* Staff */}
            <ToggleField label="Service Staff Upsell" checked={getBool("order_upsell_staff_enabled")} onChange={v => setBool("order_upsell_staff_enabled", v)} />
            {getBool("order_upsell_staff_enabled") && (
              <div className="pl-4 border-l-2 border-border space-y-3">
                <FieldRow label="Title">
                  <Input value={form.order_upsell_staff_title ?? ""} onChange={e => set("order_upsell_staff_title", e.target.value)} />
                </FieldRow>
                <FieldRow label="Description">
                  <Textarea value={form.order_upsell_staff_description ?? ""} onChange={e => set("order_upsell_staff_description", e.target.value)} rows={2} />
                </FieldRow>
              </div>
            )}
            <Separator />

            {/* Venue */}
            <ToggleField label="Venue Interest Upsell" checked={getBool("order_upsell_venue_enabled")} onChange={v => setBool("order_upsell_venue_enabled", v)} />
            {getBool("order_upsell_venue_enabled") && (
              <div className="pl-4 border-l-2 border-border space-y-3">
                <FieldRow label="Title">
                  <Input value={form.order_upsell_venue_title ?? ""} onChange={e => set("order_upsell_venue_title", e.target.value)} />
                </FieldRow>
                <FieldRow label="Description">
                  <Textarea value={form.order_upsell_venue_description ?? ""} onChange={e => set("order_upsell_venue_description", e.target.value)} rows={2} />
                </FieldRow>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        {/* Checkout & Business Rules */}
        <Collapsible>
          <SectionHeader title="Checkout & Business Rules" description="Minimum orders, lead time, copy, and defaults" />
          <CollapsibleContent className="space-y-4 pb-4">
            <FieldRow label="Checkout Heading">
              <Input value={form.order_checkout_heading ?? ""} onChange={e => set("order_checkout_heading", e.target.value)} />
            </FieldRow>
            <FieldRow label="Submit Button Text">
              <Input value={form.order_submit_button_text ?? ""} onChange={e => set("order_submit_button_text", e.target.value)} />
            </FieldRow>
            <FieldRow label="Disclaimer Text" description="Shown above the submit button">
              <Textarea value={form.order_disclaimer_text ?? ""} onChange={e => set("order_disclaimer_text", e.target.value)} rows={2} />
            </FieldRow>
            <FieldRow label="Special Requests Placeholder">
              <Input value={form.order_special_requests_placeholder ?? ""} onChange={e => set("order_special_requests_placeholder", e.target.value)} />
            </FieldRow>
            <Separator />
            <FieldRow label="Minimum Order Amount" description="Set to 0 for no minimum. Cart will show progress toward this amount.">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input type="number" min={0} className="w-32" value={form.order_minimum_amount ?? "0"} onChange={e => set("order_minimum_amount", e.target.value)} />
              </div>
            </FieldRow>
            <FieldRow label="Lead Time (days)" description="Minimum days in advance an order must be placed. Dates before this are disabled.">
              <Input type="number" min={0} className="w-32" value={form.order_lead_time_days ?? "2"} onChange={e => set("order_lead_time_days", e.target.value)} />
            </FieldRow>
            <FieldRow label="Default Guest Count">
              <Input type="number" min={1} className="w-32" value={form.order_guest_count_default ?? "20"} onChange={e => set("order_guest_count_default", e.target.value)} />
            </FieldRow>
            <Separator />
            <FieldRow label="Blackout Dates" description='JSON array of date strings, e.g. ["2026-12-25","2026-01-01"]. Orders cannot be placed for these dates.'>
              <Textarea value={form.order_blackout_dates ?? "[]"} onChange={e => set("order_blackout_dates", e.target.value)} rows={2} className="font-mono text-base md:text-xs" />
            </FieldRow>
            <FieldRow label="Available Days" description='JSON array of day abbreviations, e.g. ["mon","tue","wed","thu","fri","sat"]. Only these days are selectable.'>
              <Textarea value={form.order_available_days ?? '["mon","tue","wed","thu","fri","sat","sun"]'} onChange={e => set("order_available_days", e.target.value)} rows={2} className="font-mono text-base md:text-xs" />
            </FieldRow>
          </CollapsibleContent>
        </Collapsible>

        <Separator />

        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Save All Changes
        </Button>
      </CardContent>
    </Card>
  );
}
